import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  CLIP_STATUS_VALUES,
  MOMENT_STRATEGY_VALUES,
  retentionFor,
  type MomentStrategy,
} from '@content-engine/shared';
import { ApiError } from '../../lib/api.ts';
import {
  archiveEntity,
  bulkGenerateClips,
  fetchFilmClips,
  fetchMoments,
  selectMomentsApi,
  transcribeMomentApi,
  unarchiveEntity,
  updateMomentApi,
} from '../../lib/queries.ts';
import type { ClipListItem, ClipStatus, ClipVariant, Film, Moment } from '../../lib/types.ts';
import { ArchiveDialog, ArchivedChip, ShowArchivedCheckbox } from '../../components/archive/index.ts';
import { ClipPreview } from './ClipPreview.tsx';
import { ManualMomentPanel } from './ManualMomentPanel.tsx';

// TODO: lift to user setting once we add per-user prefs (Etap 2 territory).
const PAGE_SIZE = 20;

function pillStyle(active: boolean): CSSProperties {
  return {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'var(--mono)',
    cursor: 'pointer',
    border: active ? '1px solid var(--neon)' : '1px solid var(--line)',
    background: active ? 'var(--neon-soft)' : 'transparent',
    color: active ? 'var(--neon)' : 'var(--text-2)',
  };
}

function formatMmSs(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Красное — только настоящая беда рендера, не архив и не отсутствие файла. */
function clipStatusChip(status: ClipStatus): string {
  if (status === 'ready' || status === 'approved') return 'chip ok';
  if (status === 'error' || status === 'rejected') return 'chip err';
  return 'chip';
}

export function MomentsSection({
  film,
  defaultCount,
  defaultDurMin,
  defaultDurMax,
}: {
  film: Film;
  defaultCount: number;
  defaultDurMin: number;
  defaultDurMax: number;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filterStrategy, setFilterStrategy] = useState<MomentStrategy | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<ClipStatus>>(new Set());
  // Архивный момент без клипа не виден больше нигде (клипа нет — значит нет и
  // строки в /clips). Без этого чекбокса вернуть его из архива было бы нечем.
  const [showArchived, setShowArchived] = useState(false);

  const statusesKey = useMemo(
    () => [...selectedStatuses].sort().join(','),
    [selectedStatuses],
  );

  const momentsQuery = useQuery({
    queryKey: ['moments', film.id, filterStrategy || 'all', showArchived],
    queryFn: () =>
      fetchMoments(film.id, {
        ...(filterStrategy ? { strategy: filterStrategy } : {}),
        showArchived,
      }),
    refetchInterval: (q) => {
      if (film.status === 'selecting_moments') return 3_000;
      const list = (q.state.data?.moments ?? []) as Moment[];
      if (list.some((m) => m.transcriptionStatus === 'pending')) return 5_000;
      return false;
    },
  });

  const clipsQuery = useQuery({
    queryKey: ['filmClips', film.id, page, statusesKey],
    queryFn: () =>
      fetchFilmClips(film.id, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: [...selectedStatuses],
        include: 'variants',
        counts: true,
        // Момент — этап, а не продукт: он остаётся на странице фильма, даже если
        // его клип в архиве. Без showArchived строка момента врала бы «не нарезан».
        showArchived: true,
      }),
    refetchInterval: (q) => {
      const list = q.state.data?.clips ?? [];
      if (list.some((c) => c.status === 'pending' || c.status === 'rendering')) return 3_000;
      return false;
    },
  });

  const clips = clipsQuery.data?.clips ?? [];
  const total = clipsQuery.data?.total ?? 0;
  const counts = clipsQuery.data?.counts ?? null;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Клипы грузим с showArchived (нужно для колонки состояния клипа), а бэкенд
  // при showArchived кладёт в бандл и АРХИВНЫЕ варианты. Этим бандлом мы греем
  // общий кеш ['clipVariants', clipId] — значит архивные варианты обязаны быть
  // отброшены ЗДЕСЬ, иначе они всплывут в ленте вариантов как активные.
  const variantsBundle = useMemo(() => {
    const raw = clipsQuery.data?.variants;
    if (!raw) return null;
    return Object.fromEntries(
      Object.entries(raw).map(([clipId, vs]) => [clipId, vs.filter((v) => !v.archivedAt)]),
    ) as Record<string, ClipVariant[]>;
  }, [clipsQuery.data?.variants]);

  const lastHydratedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!variantsBundle) return;
    const hash = Object.entries(variantsBundle)
      .map(([cid, vs]) => `${cid}:${vs.map((v) => v.id).join('.')}`)
      .sort()
      .join('|');
    if (hash === lastHydratedRef.current) return;
    lastHydratedRef.current = hash;
    for (const [clipId, variants] of Object.entries(variantsBundle)) {
      queryClient.setQueryData(['clipVariants', clipId], { variants } as { variants: ClipVariant[] });
    }
  }, [variantsBundle, queryClient]);

  // Clamp page only after data has actually loaded; otherwise the initial
  // loading state (total=0, totalPages=1) would reset every Next-click.
  useEffect(() => {
    if (!clipsQuery.data) return;
    if (page >= totalPages) setPage(totalPages - 1);
  }, [totalPages, page, clipsQuery.data]);

  const toggleStatus = (s: ClipStatus): void => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
    setPage(0);
  };

  const clearStatuses = (): void => {
    setSelectedStatuses(new Set());
    setPage(0);
  };

  const clipsByMoment = new Map<string, ClipListItem>();
  for (const c of clips) {
    if (!clipsByMoment.has(c.momentId)) clipsByMoment.set(c.momentId, c);
  }

  const bulkMutation = useMutation({
    mutationFn: () =>
      bulkGenerateClips(film.id, filterStrategy ? { strategy: filterStrategy } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['filmClips', film.id] });
    },
  });

  const moments = momentsQuery.data?.moments ?? [];

  return (
    <div className="section">
      <div className="section-head">
        <h2>{t('films.detail.momentsSection')}</h2>
        <div className="filters" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {t('films.detail.momentsByStrategy')}
          </label>
          <select
            value={filterStrategy}
            onChange={(e) => setFilterStrategy(e.target.value as MomentStrategy | '')}
            style={{ fontSize: 12, padding: '2px 8px' }}
          >
            <option value="">{t('common.all')}</option>
            {MOMENT_STRATEGY_VALUES.map((s) => (
              <option key={s} value={s}>
                {t(`films.detail.strategy_${s}`)}
              </option>
            ))}
          </select>
          <ShowArchivedCheckbox checked={showArchived} onChange={setShowArchived} />
          <button
            onClick={() => bulkMutation.mutate()}
            disabled={bulkMutation.isPending || moments.length === 0}
          >
            {bulkMutation.isPending ? '…' : t('clips.generateAll')}
          </button>
          <button type="button" onClick={() => setShowPanel(true)}>
            {t('moments.manual.openButton')}
          </button>
          <button className="primary" onClick={() => setShowModal(true)}>
            {t('films.detail.selectMomentsButton')}
          </button>
        </div>
      </div>
      <div className="section-body">
        {showPanel && (
          <ManualMomentPanel
            film={film}
            onCreated={() => {
              setShowPanel(false);
              void queryClient.invalidateQueries({ queryKey: ['moments', film.id] });
            }}
            onClose={() => setShowPanel(false)}
          />
        )}
        {film.status === 'selecting_moments' && (
          <div className="working" style={{ marginBottom: 12 }}>
            <span className="spinner" />
            {t('films.detail.selecting')}
          </div>
        )}
        {moments.length === 0 && film.status !== 'selecting_moments' && (
          <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
            {t('films.detail.noMoments')}
          </div>
        )}
        {moments.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              type="button"
              onClick={clearStatuses}
              style={pillStyle(selectedStatuses.size === 0)}
            >
              {t('clips.filters.all')} ({total})
            </button>
            {CLIP_STATUS_VALUES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                style={pillStyle(selectedStatuses.has(s))}
                title={t('clips.filters.tooltipUnfiltered')}
              >
                {t(`clips.status_${s}`)} ({counts?.[s] ?? 0})
              </button>
            ))}
          </div>
        )}
        {moments.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Strategy</th>
                <th>Type</th>
                <th className="num">Score</th>
                <th>Title</th>
                <th>Themes</th>
                <th>{t('films.detail.clipState')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {moments.map((m) => (
                <MomentRow
                  key={m.id}
                  moment={m}
                  filmId={film.id}
                  clipForMoment={clipsByMoment.get(m.id)}
                />
              ))}
            </tbody>
          </table>
        )}
        {total > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              marginTop: 16,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← {t('common.prev')}
            </button>
            <span>{t('clips.pagination.pageOf', { current: page + 1, total: totalPages })}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
            >
              {t('common.next')} →
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <SelectMomentsDialog
          filmId={film.id}
          existingCount={moments.length}
          manualEditedCount={moments.filter((m) => m.manualEdited).length}
          defaultCount={defaultCount}
          defaultDurMin={defaultDurMin}
          defaultDurMax={defaultDurMax}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            void queryClient.invalidateQueries({ queryKey: ['films', film.id] });
            void queryClient.invalidateQueries({ queryKey: ['moments', film.id] });
          }}
        />
      )}
    </div>
  );
}

function MomentRow({
  moment,
  filmId,
  clipForMoment,
}: {
  moment: Moment;
  filmId: string;
  clipForMoment?: ClipListItem;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [startSec, setStartSec] = useState(Number(moment.startSec));
  const [endSec, setEndSec] = useState(Number(moment.endSec));
  const [title, setTitle] = useState(moment.title);

  const updateMutation = useMutation({
    mutationFn: () => updateMomentApi(moment.id, { startSec, endSec, title }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['moments', filmId] });
    },
  });

  const transcribeMutation = useMutation({
    mutationFn: () => transcribeMomentApi(moment.id),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['moments', filmId] }); },
  });

  const invalidateArchive = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['moments', filmId] });
    void queryClient.invalidateQueries({ queryKey: ['filmClips', filmId] });
    void queryClient.invalidateQueries({ queryKey: ['clips'] });
  };

  // Момент и клип — одна сущность: бэкенд архивирует пару, какой бы конец мы
  // ни назвали. Зовём 'moment' — он есть всегда, даже когда клип ещё не нарезан.
  const archiveMutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) => archiveEntity('moment', moment.id, opts),
    onSuccess: () => {
      setShowArchiveDialog(false);
      invalidateArchive();
    },
    onError: (e: Error) => alert(e.message),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveEntity('moment', moment.id),
    onSuccess: invalidateArchive,
    onError: (e: Error) => alert(e.message),
  });

  // Клипа ещё нет → нечего терять и нечего освобождать: диалог не должен врать
  // про «клип исчезнет из списка клипов».
  const archived = moment.archivedAt !== null;
  const archiveDialog = showArchiveDialog ? (
    <ArchiveDialog
      kind={clipForMoment ? 'clip' : 'moment'}
      freeAfterDays={retentionFor('clip_foundation')}
      sizeBytes={clipForMoment?.file.sizeBytes ?? null}
      variantsCount={clipForMoment?.variantsCount ?? 0}
      busy={archiveMutation.isPending}
      onCancel={() => setShowArchiveDialog(false)}
      onConfirm={(opts) => archiveMutation.mutate(opts)}
    />
  ) : null;

  if (editing) {
    return (
      <>
        <tr>
          <td>
            <input type="number" value={startSec} onChange={(e) => setStartSec(Number(e.target.value))} style={{ width: 70 }} />
            {' - '}
            <input type="number" value={endSec} onChange={(e) => setEndSec(Number(e.target.value))} style={{ width: 70 }} />
          </td>
          <td colSpan={6}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: '90%' }} />
          </td>
          <td>
            <button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
              {t('common.save')}
            </button>
            <button onClick={() => setEditing(false)} style={{ marginLeft: 4 }}>
              {t('common.cancel')}
            </button>
          </td>
        </tr>
        <tr>
          <td colSpan={8} style={{ borderTop: 'none', paddingTop: 0 }}>
            {moment.transcriptionStatus === 'transcribed' ? (
              <ClipPreview momentId={moment.id} filmId={filmId} clip={clipForMoment} />
            ) : (
              <div style={{ padding: 12, color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>
                {t('moments.notTranscribedPlaceholder')}
              </div>
            )}
          </td>
        </tr>
      </>
    );
  }

  return (
    <>
      <tr className={archived ? 'arch' : undefined}>
        <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
          {formatMmSs(Number(moment.startSec))} - {formatMmSs(Number(moment.endSec))}
          {archived && (
            <div style={{ marginTop: 4 }}>
              <ArchivedChip archivedAt={moment.archivedAt} />
            </div>
          )}
        </td>
        <td>
          <span className="pill">{t(`films.detail.strategy_${moment.strategy}`)}</span>
        </td>
        <td>
          <span className="pill">{t(`films.detail.momentType_${moment.momentType}`)}</span>
        </td>
        <td className="num">{moment.aiScore}</td>
        <td>
          <div style={{ color: 'var(--neon)' }}>{moment.title}</div>
          {moment.whyInteresting && (
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{moment.whyInteresting}</div>
          )}
          {moment.manualEdited && (
            <span style={{ fontSize: 10, color: 'var(--amber)', marginTop: 2 }}>
              ({t('films.detail.manualEdited')})
            </span>
          )}
        </td>
        <td style={{ fontSize: 11, color: 'var(--text-2)' }}>{moment.themes.join(', ') || '—'}</td>
        {/* Состояние клипа момента: он живёт в /clips своей жизнью, здесь — только статус. */}
        <td>
          {!clipForMoment ? (
            <span style={{ color: 'var(--text-3)' }}>{t('films.detail.clipNotCut')}</span>
          ) : clipForMoment.archivedAt ? (
            <ArchivedChip archivedAt={clipForMoment.archivedAt} />
          ) : (
            <span className={clipStatusChip(clipForMoment.status)}>
              {t(`clips.status_${clipForMoment.status}`)}
            </span>
          )}
          {clipForMoment && (
            <div style={{ marginTop: 4 }}>
              <Link to={`/clips/${clipForMoment.id}`} style={{ fontSize: 11, color: 'var(--neon)' }}>
                {t('films.detail.openClip')}
              </Link>
            </div>
          )}
        </td>
        <td>
          {moment.transcriptionStatus === 'transcribed' && (
            <span className="pill live">✓ {t('moments.status.transcribed')}</span>
          )}
          {moment.transcriptionStatus === 'pending' && (
            <span className="pill idle"><span className="spinner" />{t('moments.status.pending')}</span>
          )}
          {moment.transcriptionStatus === 'none' && (
            <button
              type="button"
              onClick={() => transcribeMutation.mutate()}
              disabled={transcribeMutation.isPending}
            >
              📝 {t('moments.status.transcribe')}
            </button>
          )}
          {moment.transcriptionStatus === 'error' && (
            <button
              type="button"
              onClick={() => transcribeMutation.mutate()}
              disabled={transcribeMutation.isPending}
              title={moment.transcriptionError ?? undefined}
              style={{ color: 'var(--hot)' }}
            >
              ✕ {t('moments.status.retry')}
            </button>
          )}
          {' '}
          <button onClick={() => setEditing(true)} style={{ marginRight: 4 }}>
            {t('common.edit')}
          </button>
          {/* Вместо удаления — архив: момент вместе со своим клипом уходит из
              рабочих списков, варианты клипа остаются активными. Архивный
              момент возвращается отсюда же — другого места у него нет. */}
          {archived || clipForMoment?.archivedAt ? (
            <button
              type="button"
              onClick={() => unarchiveMutation.mutate()}
              disabled={unarchiveMutation.isPending}
              style={{ fontSize: 11, padding: '2px 8px' }}
            >
              {t('films.detail.unarchive')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowArchiveDialog(true)}
              style={{ fontSize: 11, padding: '2px 8px' }}
            >
              {t('films.archiveButton')}
            </button>
          )}
          {/* Модалка живёт в ячейке (position:fixed), чтобы не ломать вёрстку tbody. */}
          {archiveDialog}
        </td>
      </tr>
      <tr>
        <td colSpan={8} style={{ borderTop: 'none', paddingTop: 0 }}>
          {moment.transcriptionStatus === 'transcribed' ? (
            <ClipPreview momentId={moment.id} filmId={filmId} clip={clipForMoment} />
          ) : (
            <div style={{ padding: 12, color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>
              {t('moments.notTranscribedPlaceholder')}
            </div>
          )}
        </td>
      </tr>
    </>
  );
}

function SelectMomentsDialog(props: {
  filmId: string;
  existingCount: number;
  manualEditedCount: number;
  defaultCount: number;
  defaultDurMin: number;
  defaultDurMax: number;
  onClose: () => void;
  onSuccess: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [strategy, setStrategy] = useState<MomentStrategy>('mixed');
  const [count, setCount] = useState(props.defaultCount);
  const [durMin, setDurMin] = useState(props.defaultDurMin);
  const [durMax, setDurMax] = useState(props.defaultDurMax);
  const [customPrompt, setCustomPrompt] = useState('');

  const selectMutation = useMutation({
    mutationFn: () =>
      selectMomentsApi(props.filmId, {
        strategy,
        count,
        durMinSec: durMin,
        durMaxSec: durMax,
        customPrompt: strategy === 'custom' ? customPrompt : undefined,
      }),
    onSuccess: () => props.onSuccess(),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    selectMutation.mutate();
  }

  const showWarning = props.existingCount > 0;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 560,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 28,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{t('films.detail.selectModal.title')}</h2>
        <form onSubmit={submit}>
          <div className="form-row">
            <label>{t('films.detail.selectModal.strategyLabel')}</label>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value as MomentStrategy)}>
              {MOMENT_STRATEGY_VALUES.map((s) => (
                <option key={s} value={s}>
                  {t(`films.detail.strategy_${s}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row inline">
            <div>
              <label>{t('films.detail.selectModal.countLabel')}</label>
              <input type="number" min={1} max={10} value={count} onChange={(e) => setCount(Number(e.target.value))} />
            </div>
            <div>
              <label>{t('films.detail.selectModal.durMinLabel')}</label>
              <input type="number" min={5} max={120} value={durMin} onChange={(e) => setDurMin(Number(e.target.value))} />
            </div>
            <div>
              <label>{t('films.detail.selectModal.durMaxLabel')}</label>
              <input type="number" min={10} max={180} value={durMax} onChange={(e) => setDurMax(Number(e.target.value))} />
            </div>
          </div>
          {strategy === 'custom' && (
            <div className="form-row">
              <label>{t('films.detail.selectModal.customPromptLabel')}</label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={4}
                placeholder={t('films.detail.selectModal.customPromptPlaceholder')}
                required={strategy === 'custom'}
              />
            </div>
          )}
          {showWarning && (
            <div
              style={{
                padding: '8px 12px',
                background: 'rgba(255,180,0,0.1)',
                border: '1px solid rgba(255,180,0,0.3)',
                borderRadius: 6,
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {t('films.detail.selectModal.overwriteWarning', {
                existing: props.existingCount,
                edited: props.manualEditedCount,
              })}
            </div>
          )}
          {selectMutation.error instanceof ApiError && (
            <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 12 }}>
              {selectMutation.error.message}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={props.onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={selectMutation.isPending}>
              {selectMutation.isPending
                ? t('films.detail.selectModal.submitting')
                : t('films.detail.selectModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
